/* =============================================================
   useStudentById.js — Fetch a single student by UUID
   -------------------------------------------------------------
   Used by the admin profile pages (GenericIncomingProfile,
   GenericOutgoingProfile) to load a student's details from
   Supabase by their UUID.

   Cache strategy (module-level Map):
     _studentCache survives React component unmount/remount and
     Supabase token refreshes. On every mount the hook returns
     cached data immediately (no spinner), then silently
     re-fetches in the background to stay fresh.

   Usage:
     const { student, loading } = useStudentById(id, 'incoming');
     const { student, loading } = useStudentById(id, 'outgoing');

   The shape of the returned student object depends on the type:

     type = 'incoming':
       { id, name, uni, country, status, deadline, email }

     type = 'outgoing':
       { id, name, dest, country, status, la, urgent, deadline, email }
   ============================================================= */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

const FETCH_TIMEOUT_MS = 8000;


/* ─── MODULE-LEVEL CACHE ─────────────────────────────────────── */
// Key: `${id}:${type}` → mapped student object
const _studentCache = new Map();


export function useStudentById(id, type) {
  const cacheKey = id ? `${id}:${type}` : null;
  const _cached  = cacheKey ? _studentCache.get(cacheKey) : undefined;

  // Initialize from cache immediately — no spinner on re-navigation
  const [student, setStudent] = useState(_cached ?? null);
  const [loading, setLoading] = useState(_cached === undefined);

  const abortRef = useRef(null);

  useEffect(() => {
    if (!id) { setLoading(false); return; }

    // Cancel any previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    const timeoutId = setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    // Show spinner only on the very first fetch for this student
    if (!_studentCache.has(cacheKey)) setLoading(true);

    (async () => {
      try {
        const { data, error } = await supabase
          .from('students')
          .select('*')
          .eq('id', id)
          .eq('type', type)
          .maybeSingle()
          .abortSignal(signal);

        if (signal.aborted) return;

        if (error) {
          console.error('[useStudentById] fetch:', error.message);
        } else if (data) {
          let mapped;

          if (type === 'incoming') {
            mapped = {
              id:                  data.id,
              name:                data.name,
              uni:                 data.home_university ?? '—',
              country:             data.country,
              nationality:         data.nationality         ?? null,
              exchangeType:        data.exchange_type       ?? null,
              euStatus:            data.eu_status           ?? null,
              degreeLevel:         data.degree_level        ?? null,
              degreeLevelCustom:   data.degree_level_custom ?? null,
              processTemplateId:   data.process_template_id ?? null,
              semesterId:          data.semester_id         ?? null,
              status:              data.status,
              partnerId:           data.partner_id          ?? null,
              deadline: data.deadline
                ? new Date(data.deadline + 'T00:00:00')
                    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : '—',
              email: data.email ?? null,
            };
          } else {
            mapped = {
              id:                  data.id,
              name:                data.name,
              dest:                data.destination_university ?? '—',
              country:             data.country,
              nationality:         data.nationality         ?? null,
              exchangeType:        data.exchange_type       ?? null,
              euStatus:            data.eu_status           ?? null,
              degreeLevel:         data.degree_level        ?? null,
              degreeLevelCustom:   data.degree_level_custom ?? null,
              processTemplateId:   data.process_template_id ?? null,
              semesterId:          data.semester_id         ?? null,
              status:              data.status,
              partnerId:           data.partner_id          ?? null,
              la:                  data.learning_agreement_status ?? 'Not started',
              urgent:              data.is_urgent ?? false,
              deadline: data.deadline
                ? new Date(data.deadline + 'T00:00:00')
                    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : '—',
              email: data.email ?? null,
            };
          }

          setStudent(mapped);
          _studentCache.set(cacheKey, mapped);
        } else {
          setStudent(null);
        }
      } catch (err) {
        if (signal.aborted) return;
        console.error('[useStudentById] unexpected:', err);
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    })();

    return () => {
      abortRef.current?.abort();
    };
  }, [id, type, cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── updateStudent ──────────────────────────────────────────── */
  // fields: any subset of { name, email, home_university,
  //                         destination_university, country }
  const updateStudent = async (fields) => {
    if (!id) return;
    const { error: updateError } = await supabase
      .from('students')
      .update(fields)
      .eq('id', id);

    if (updateError) throw updateError;

    // Patch local state and cache immediately
    setStudent(prev => {
      if (!prev) return prev;
      const next = {
        ...prev,
        ...(fields.name                    !== undefined && { name:                fields.name }),
        ...(fields.email                   !== undefined && { email:               fields.email || null }),
        ...(fields.home_university         !== undefined && { uni:                 fields.home_university || '—' }),
        ...(fields.destination_university  !== undefined && { dest:                fields.destination_university || '—' }),
        ...(fields.country                 !== undefined && { country:             fields.country }),
        ...(fields.degree_level            !== undefined && { degreeLevel:         fields.degree_level || null }),
        ...(fields.degree_level_custom     !== undefined && { degreeLevelCustom:   fields.degree_level_custom || null }),
        ...(fields.process_template_id     !== undefined && { processTemplateId:   fields.process_template_id || null }),
        ...(fields.semester_id             !== undefined && { semesterId:          fields.semester_id || null }),
      };
      if (cacheKey) _studentCache.set(cacheKey, next);
      return next;
    });
  };

  return { student, loading, updateStudent };
}
